import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { db, collection, query, where, onSnapshot, orderBy } from '../firebase';
import { products } from '../data';
import toast from 'react-hot-toast';
import './UserProfile.css';

const TABS = [
  { id: 'profile',  label: 'Profile',    icon: '👤' },
  { id: 'edit',     label: 'Edit Profile',icon: '✏️' },
  { id: 'orders',   label: 'My Orders',   icon: '📦' },
  { id: 'wishlist', label: 'Wishlist',    icon: '❤️' },
  { id: 'settings', label: 'Settings',   icon: '⚙️' },
];

export default function UserProfile({ navigate }) {
  const { user, logout, updateUserProfile } = useAuth();
  const [tab,         setTab]         = useState('profile');
  const [orders,      setOrders]      = useState([]);
  const [editForm,    setEditForm]    = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [photoPreview,setPhotoPreview]= useState('');
  const [photoFile,   setPhotoFile]   = useState(null);
  const [wishlistIds, setWishlistIds] = useState(
    JSON.parse(localStorage.getItem('freshmart_wishlist') || '[]')
  );
  const fileInputRef = useRef();

  // Pre-fill edit form when user loads
  useEffect(() => {
    if (user) {
      setEditForm({
        name:    user.name    || '',
        phone:   user.phone   || '',
        address: user.address || '',
        city:    user.city    || '',
        pincode: user.pincode || '',
      });
      setPhotoPreview(user.photoURL || '');
    }
  }, [user]);

  // Load orders from Firestore in real-time
  useEffect(() => {
    if (!user?.uid) return;
    try {
      const q = query(
        collection(db, 'orders'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      const unsub = onSnapshot(q, snap => {
        setOrders(snap.docs.map(d => ({ fireId: d.id, ...d.data() })));
      });
      return unsub;
    } catch { return undefined; }
  }, [user?.uid]);

  if (!user) { navigate('login'); return null; }

  const wishlistProducts = products.filter(p => wishlistIds.includes(p.id));
  const totalSpent = orders.reduce((s, o) => s + (o.total || 0), 0);

  // ── Photo picker ───────────────────────────────────────────
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2MB'); return; }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = ev => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  // ── Save profile ───────────────────────────────────────────
  const handleSaveProfile = async () => {
    if (!editForm.name.trim()) { toast.error('Name is required'); return; }
    if (editForm.phone && !/^\d{10}$/.test(editForm.phone)) {
      toast.error('Enter valid 10-digit phone number'); return;
    }
    setEditLoading(true);

    let photoURL = user.photoURL || '';
    // Convert photo to base64 and store in Firestore (no Storage needed)
    if (photoFile) {
      const reader = new FileReader();
      photoURL = await new Promise(resolve => {
        reader.onload = e => resolve(e.target.result);
        reader.readAsDataURL(photoFile);
      });
    }

    const res = await updateUserProfile({
      name:    editForm.name.trim(),
      phone:   editForm.phone.trim(),
      address: editForm.address.trim(),
      city:    editForm.city.trim(),
      pincode: editForm.pincode.trim(),
      photoURL,
    });

    setEditLoading(false);
    if (res.success) {
      toast.success('✅ Profile updated successfully!');
      setTab('profile');
      setPhotoFile(null);
    } else {
      toast.error('Failed: ' + res.error);
    }
  };

  const removeWishlistItem = (id) => {
    const updated = wishlistIds.filter(wid => wid !== id);
    localStorage.setItem('freshmart_wishlist', JSON.stringify(updated));
    setWishlistIds(updated);
    toast('Removed from wishlist');
  };

  const fmtDate = (ts) => {
    if (!ts) return '-';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="page profile-page">
      <div className="container">

        {/* ── Hero ── */}
        <div className="profile-hero">
          <div className="profile-hero-bg" />

          {/* Avatar */}
          <div className="profile-avatar-wrap">
            {user.photoURL ? (
              <img src={user.photoURL} alt="Profile" className="profile-avatar-img" />
            ) : (
              <div className="profile-avatar-big">{user.avatar}</div>
            )}
            <button className="change-photo-btn" onClick={() => { setTab('edit'); setTimeout(()=>fileInputRef.current?.click(),200); }}>
              📷
            </button>
          </div>

          <div className="profile-hero-info">
            <h1>{user.name}</h1>
            <p className="profile-email">{user.email}</p>
            {user.phone && <p className="profile-phone">📞 {user.phone}</p>}
            {user.city  && <p className="profile-location">📍 {user.city}{user.pincode ? ` - ${user.pincode}` : ''}</p>}
            <div className="profile-stats">
              <div><strong>{orders.length}</strong><span>Orders</span></div>
              <div><strong>{wishlistIds.length}</strong><span>Wishlist</span></div>
              <div><strong>₹{totalSpent.toLocaleString()}</strong><span>Spent</span></div>
            </div>
          </div>

          <div className="profile-hero-actions">
            <button className="edit-hero-btn" onClick={() => setTab('edit')}>✏️ Edit Profile</button>
            {user.role === 'admin' && (
              <button className="admin-panel-btn" onClick={() => navigate('admin')}>👑 Admin Panel</button>
            )}
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="profile-tabs">
          {TABS.map(t => (
            <button key={t.id} className={`profile-tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
              <span>{t.icon}</span> {t.label}
            </button>
          ))}
        </div>

        <div className="profile-content">

          {/* ════ PROFILE VIEW ════ */}
          {tab === 'profile' && (
            <div className="profile-view-card">
              <div className="pvc-header">
                <h3>Personal Information</h3>
                <button className="pvc-edit-btn" onClick={() => setTab('edit')}>✏️ Edit</button>
              </div>
              <div className="info-grid">
                {[
                  { icon:'👤', label:'Full Name',    value: user.name },
                  { icon:'✉️', label:'Email Address', value: user.email },
                  { icon:'📞', label:'Phone Number',  value: user.phone  || <span className="not-set">Not set</span> },
                  { icon:'🏙️', label:'City',          value: user.city   || <span className="not-set">Not set</span> },
                  { icon:'📍', label:'Address',        value: user.address|| <span className="not-set">Not set</span> },
                  { icon:'📮', label:'Pincode',        value: user.pincode|| <span className="not-set">Not set</span> },
                  { icon:'🔑', label:'Role',           value: <span className={`role-chip ${user.role}`}>{user.role}</span> },
                  { icon:'🆔', label:'User ID',        value: <code>{user.uid?.slice(0,16)}…</code> },
                ].map(item => (
                  <div key={item.label} className="info-item">
                    <p className="info-label">{item.icon} {item.label}</p>
                    <p className="info-value">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ════ EDIT PROFILE ════ */}
          {tab === 'edit' && (
            <div className="edit-profile-card">
              <h3>✏️ Edit Your Profile</h3>

              {/* Photo Upload */}
              <div className="photo-upload-section">
                <div className="photo-upload-preview">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="photo-preview-img" />
                  ) : (
                    <div className="photo-placeholder">
                      <span>{user.avatar}</span>
                    </div>
                  )}
                  <div className="photo-upload-overlay" onClick={() => fileInputRef.current?.click()}>
                    <span>📷</span>
                    <p>Change Photo</p>
                  </div>
                </div>
                <div className="photo-upload-info">
                  <h4>Profile Photo</h4>
                  <p>Upload a clear photo. Max size: 2MB.<br/>Supported: JPG, PNG, GIF</p>
                  <button className="upload-photo-btn" onClick={() => fileInputRef.current?.click()}>
                    📁 Choose Photo
                  </button>
                  {photoPreview && photoPreview !== user.photoURL && (
                    <button className="remove-photo-btn" onClick={() => { setPhotoPreview(user.photoURL||''); setPhotoFile(null); }}>
                      ✕ Remove
                    </button>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  style={{ display:'none' }}
                  onChange={handlePhotoChange}
                />
              </div>

              {/* Edit Form */}
              <div className="edit-form-grid">
                <div className="edit-form-group">
                  <label>👤 Full Name *</label>
                  <input
                    type="text"
                    placeholder="Your full name"
                    value={editForm.name}
                    onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  />
                </div>
                <div className="edit-form-group">
                  <label>✉️ Email Address</label>
                  <input type="email" value={user.email} disabled className="disabled-input" />
                  <small>Email cannot be changed</small>
                </div>
                <div className="edit-form-group">
                  <label>📞 Phone Number</label>
                  <input
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={editForm.phone}
                    onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                    maxLength={10}
                  />
                </div>
                <div className="edit-form-group">
                  <label>🏙️ City</label>
                  <input
                    type="text"
                    placeholder="e.g. Pune"
                    value={editForm.city}
                    onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))}
                  />
                </div>
                <div className="edit-form-group full">
                  <label>📍 Delivery Address</label>
                  <textarea
                    rows={3}
                    placeholder="Flat no, Building, Street, Area..."
                    value={editForm.address}
                    onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))}
                  />
                </div>
                <div className="edit-form-group">
                  <label>📮 Pincode</label>
                  <input
                    type="text"
                    placeholder="e.g. 411001"
                    value={editForm.pincode}
                    onChange={e => setEditForm(f => ({ ...f, pincode: e.target.value }))}
                    maxLength={6}
                  />
                </div>
              </div>

              <div className="edit-form-actions">
                <button className="cancel-edit-btn" onClick={() => { setTab('profile'); setPhotoPreview(user.photoURL||''); setPhotoFile(null); }}>
                  ✕ Cancel
                </button>
                <button className="save-profile-btn" onClick={handleSaveProfile} disabled={editLoading}>
                  {editLoading
                    ? <><span className="spin-icon">⏳</span> Saving…</>
                    : '✅ Save Changes'
                  }
                </button>
              </div>
            </div>
          )}

          {/* ════ MY ORDERS ════ */}
          {tab === 'orders' && (
            <div className="orders-list">
              {orders.length === 0 ? (
                <div className="empty-tab">
                  <span>📦</span>
                  <h3>No orders yet</h3>
                  <p>Your orders will appear here in real-time</p>
                  <button className="btn-primary-sm" onClick={() => navigate('products')}>Start Shopping</button>
                </div>
              ) : orders.map(o => (
                <div key={o.fireId} className="order-card">
                  <div className="order-card-header">
                    <div>
                      <p className="order-id">#{o.id?.slice(-10) || o.fireId?.slice(0,10)}</p>
                      <p className="order-date">{fmtDate(o.createdAt)}</p>
                    </div>
                    <div className="order-card-right">
                      <span className={`order-status-badge status-${o.status}`}>{o.status?.replace(/_/g,' ')}</span>
                      <p className="order-total">₹{o.total?.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="order-items-preview">
                    {o.items?.slice(0,4).map((item,i) => (
                      <div key={i} className="order-item-chip">
                        <img src={item.image} alt={item.name} onError={e=>e.target.style.display='none'} />
                        <span>{item.name} ×{item.qty}</span>
                      </div>
                    ))}
                    {o.items?.length > 4 && <span className="more-chip">+{o.items.length-4} more</span>}
                  </div>
                  <div className="order-card-footer">
                    <span>📍 {o.customer?.address?.slice(0,45)}{o.customer?.address?.length>45?'…':''}</span>
                    <button onClick={() => navigate('track', {...o, createdAt: o.createdAt?.toDate?.()?.toISOString?.() || new Date().toISOString()})} className="track-btn">
                      Track Order →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ════ WISHLIST ════ */}
          {tab === 'wishlist' && (
            <div>
              {wishlistProducts.length === 0 ? (
                <div className="empty-tab">
                  <span>❤️</span>
                  <h3>Your wishlist is empty</h3>
                  <p>Tap the 🤍 heart on any product to save it here</p>
                  <button className="btn-primary-sm" onClick={() => navigate('products')}>Explore Products</button>
                </div>
              ) : (
                <>
                  <p className="wishlist-count">{wishlistProducts.length} saved items</p>
                  <div className="wishlist-grid">
                    {wishlistProducts.map(p => (
                      <div key={p.id} className="wishlist-card">
                        <img src={p.image} alt={p.name} onError={e=>e.target.style.display='none'} />
                        <div className="wc-info">
                          <p className="wc-name">{p.name}</p>
                          <p className="wc-price">₹{p.price}/{p.unit}</p>
                          <button className="wc-add" onClick={() => navigate('products')}>Add to Cart</button>
                        </div>
                        <button className="wc-remove" onClick={() => removeWishlistItem(p.id)}>✕</button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ════ SETTINGS ════ */}
          {tab === 'settings' && (
            <div className="settings-card">
              <h3>Account Settings</h3>

              <div className="settings-section">
                <h4>🔔 Notifications</h4>
                {['Order Updates','Delivery Alerts','Offers & Promotions','New Arrivals'].map(s => (
                  <label key={s} className="setting-toggle">
                    <span>{s}</span>
                    <div className="toggle-switch">
                      <input type="checkbox" defaultChecked={s !== 'New Arrivals'} onChange={() => toast(`${s} preference saved`)} />
                      <span className="ts-slider" />
                    </div>
                  </label>
                ))}
              </div>

              <div className="settings-section">
                <h4>🔐 Security</h4>
                <button className="settings-btn" onClick={() => { setTab('edit'); }}>
                  ✏️ Edit Profile & Photo
                </button>
                <button className="settings-btn" onClick={() => toast('Password reset email sent! Check your inbox.')}>
                  🔑 Change Password
                </button>
              </div>

              <div className="settings-section">
                <h4>📱 App</h4>
                <button className="settings-btn" onClick={() => { localStorage.removeItem('freshmart_wishlist'); setWishlistIds([]); toast('Wishlist cleared'); }}>
                  🗑 Clear Wishlist
                </button>
              </div>

              <div className="settings-section">
                <h4>⚠️ Danger Zone</h4>
                <button className="settings-btn danger" onClick={() => { logout(); navigate('login'); toast.success('Logged out!'); }}>
                  🚪 Logout
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
